<template>
  <div class="players-page">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.players.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.players.subtitle') }}</p>
      </div>
      <div class="header-actions">
        <!-- Bulk Actions (visible when players selected) -->
        <template v-if="selectedIds.size > 0">
          <span class="selected-count">{{ $t('admin.players.n_selected', { n: selectedIds.size }) }}</span>
          <div class="bulk-actions-wrapper" @click.stop>
            <button class="btn btn-secondary" @click="toggleBulkMenu">
              {{ $t('admin.players.bulk_actions') }}
              <span class="dropdown-arrow">{{ bulkMenuOpen ? '\u25B2' : '\u25BC' }}</span>
            </button>
            <div v-if="bulkMenuOpen" class="bulk-dropdown">
              <button class="bulk-dropdown-item" @click="openBulkAction('suspend')">
                <span class="bulk-icon"><AppIcon name="action-suspend" :size="14" /></span> {{ $t('admin.players.bulk_suspend_selected') }}
              </button>
              <button class="bulk-dropdown-item bulk-dropdown-danger" @click="openBulkAction('ban')">
                <span class="bulk-icon"><AppIcon name="action-ban" :size="14" /></span> {{ $t('admin.players.bulk_ban_selected') }}
              </button>
              <div class="bulk-dropdown-divider"></div>
              <button class="bulk-dropdown-item" @click="exportSelectedCsv">
                <span class="bulk-icon"><AppIcon name="action-export" :size="14" /></span> {{ $t('admin.players.export_csv') }}
              </button>
            </div>
          </div>
          <button class="btn btn-ghost" @click="clearSelection">{{ $t('admin.players.clear_selection') }}</button>
        </template>
        <button v-else class="btn btn-secondary" @click="exportCsv">{{ $t('admin.players.export_csv') }}</button>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="filter-bar">
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.players.filter_search_label') }}</label>
        <input
          v-model="filters.search"
          type="text"
          class="filter-input"
          :placeholder="$t('admin.players.filter_search_placeholder')"
          @input="debouncedFetch"
        />
      </div>
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.players.filter_status_label') }}</label>
        <select v-model="filters.status" @change="resetAndFetch">
          <option value="">{{ $t('admin.players.filter_all') }}</option>
          <option value="active">{{ $t('admin.players.filter_active') }}</option>
          <option value="suspended">{{ $t('admin.players.filter_suspended') }}</option>
          <option value="banned">{{ $t('admin.players.filter_banned') }}</option>
        </select>
      </div>
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.players.filter_kyc_label') }}</label>
        <select v-model="filters.kyc_status" @change="resetAndFetch">
          <option value="">{{ $t('admin.players.filter_all') }}</option>
          <option value="verified">{{ $t('admin.players.filter_kyc_verified') }}</option>
          <option value="pending">{{ $t('admin.players.filter_kyc_pending') }}</option>
          <option value="unverified">{{ $t('admin.players.filter_kyc_unverified') }}</option>
          <option value="rejected">{{ $t('admin.players.filter_kyc_rejected') }}</option>
        </select>
      </div>
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.players.filter_vip_label') }}</label>
        <select v-model="filters.vip_level" @change="resetAndFetch">
          <option value="">{{ $t('admin.players.filter_all') }}</option>
          <option value="0">{{ $t('admin.players.filter_vip_standard') }}</option>
          <option value="1">{{ $t('common.vip.bronze') }}</option>
          <option value="2">{{ $t('common.vip.silver') }}</option>
          <option value="3">{{ $t('common.vip.gold') }}</option>
          <option value="4">{{ $t('common.vip.platinum') }}</option>
          <option value="5">{{ $t('common.vip.diamond') }}</option>
        </select>
      </div>
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.players.filter_joined_label') }}</label>
        <select v-model="joinedFilter" @change="applyJoinedFilter">
          <option value="">{{ $t('admin.players.filter_all_time') }}</option>
          <option value="7d">{{ $t('admin.players.filter_last_7d') }}</option>
          <option value="30d">{{ $t('admin.players.filter_last_30d') }}</option>
          <option value="90d">{{ $t('admin.players.filter_last_90d') }}</option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <span class="spinner"></span>
      {{ $t('admin.players.loading') }}
    </div>

    <!-- Players Table -->
    <div v-else class="table-container">
      <table>
        <thead>
          <tr>
            <th class="col-checkbox">
              <input
                type="checkbox"
                :checked="allPageSelected"
                :indeterminate="somePageSelected"
                @change="toggleSelectAll"
              />
            </th>
            <th class="col-player">{{ $t('admin.players.col_player') }}</th>
            <th class="col-email">{{ $t('admin.players.col_email') }}</th>
            <th class="col-balance sortable" :class="sortClass('balance')" @click="toggleSort('balance')">
              {{ $t('admin.players.col_balance') }}
              <span class="sort-icon">{{ sortIcon('balance') }}</span>
            </th>
            <th class="col-wagered sortable" :class="sortClass('total_wagered')" @click="toggleSort('total_wagered')">
              {{ $t('admin.players.col_wagered') }}
              <span class="sort-icon">{{ sortIcon('total_wagered') }}</span>
            </th>
            <th class="col-status">{{ $t('admin.players.col_status') }}</th>
            <th class="col-kyc">{{ $t('admin.players.col_kyc') }}</th>
            <th class="col-joined sortable" :class="sortClass('created_at')" @click="toggleSort('created_at')">
              {{ $t('admin.players.col_joined') }}
              <span class="sort-icon">{{ sortIcon('created_at') }}</span>
            </th>
            <th class="col-actions">{{ $t('admin.players.col_actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="players.length === 0">
            <td colspan="9" class="empty-row">{{ $t('admin.players.no_players') }}</td>
          </tr>
          <tr
            v-for="player in players"
            :key="player.id"
            class="player-row"
            :class="{ 'row-selected': selectedIds.has(player.id) }"
            @click="goToPlayer(player.id)"
          >
            <td @click.stop>
              <input
                type="checkbox"
                :checked="selectedIds.has(player.id)"
                @change="toggleSelectPlayer(player.id)"
              />
            </td>
            <td>
              <div class="player-info">
                <div class="player-avatar">{{ getInitials(player.username) }}</div>
                <div>
                  <div class="player-name">{{ player.username }}</div>
                  <div class="player-sub">@player</div>
                </div>
              </div>
            </td>
            <td>{{ player.email }}</td>
            <td>
              <span class="mono-value">{{ formatCents(player.balance) }}</span>
            </td>
            <td>
              <span class="mono-value" :class="wagerClass(player.total_wagered)">{{ formatCents(player.total_wagered) }}</span>
            </td>
            <td>
              <span class="badge" :class="statusBadgeClass(player.status)">{{ player.status }}</span>
            </td>
            <td>
              <span class="badge" :class="kycBadgeClass(player.kyc_status)">{{ player.kyc_status }}</span>
            </td>
            <td>{{ formatDate(player.created_at) }}</td>
            <td>
              <div class="action-buttons" @click.stop>
                <button class="btn-icon" :title="$t('admin.players.action_view')" @click="goToPlayer(player.id)"><AppIcon name="action-view" :size="14" /></button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="totalPages > 0" class="pagination">
        <button
          class="pagination-btn"
          :disabled="currentPage <= 1"
          @click="goToPage(currentPage - 1)"
        >&larr;</button>
        <button
          v-for="page in visiblePages"
          :key="page"
          class="pagination-btn"
          :class="{ active: page === currentPage }"
          @click="goToPage(page)"
        >{{ page }}</button>
        <span class="pagination-info">
          {{ $t('admin.players.pagination_info', { start: rangeStart, end: rangeEnd, total: totalPlayers }) }}
        </span>
        <button
          class="pagination-btn"
          :disabled="currentPage >= totalPages"
          @click="goToPage(currentPage + 1)"
        >&rarr;</button>
      </div>
    </div>

    <!-- Bulk Action Confirmation Modal -->
    <div v-if="bulkModal.visible" class="modal-overlay" @click.self="closeBulkModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">{{ bulkModal.action === 'suspend'
            ? (selectedIds.size > 1 ? $t('admin.players.bulk_modal_suspend_title_plural', { n: selectedIds.size }) : $t('admin.players.bulk_modal_suspend_title', { n: selectedIds.size }))
            : (selectedIds.size > 1 ? $t('admin.players.bulk_modal_ban_title_plural', { n: selectedIds.size }) : $t('admin.players.bulk_modal_ban_title', { n: selectedIds.size })) }}</h3>
          <button class="modal-close" @click="closeBulkModal">&times;</button>
        </div>
        <div class="modal-body">
          <p class="modal-description">
            {{ bulkModal.action === 'suspend'
              ? $t('admin.players.bulk_modal_suspend_desc')
              : $t('admin.players.bulk_modal_ban_desc') }}
          </p>
          <div class="modal-affected">
            <div class="affected-label">{{ $t('admin.players.bulk_modal_affected') }}</div>
            <div class="affected-list">
              <span v-for="player in selectedPlayers" :key="player.id" class="affected-player">
                {{ player.username }}
              </span>
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.players.bulk_modal_reason_label') }} <span class="required">*</span></label>
            <textarea
              v-model="bulkModal.reason"
              class="form-textarea"
              :placeholder="$t('admin.players.bulk_modal_reason_placeholder')"
              rows="3"
              maxlength="500"
            ></textarea>
            <div class="char-count">{{ bulkModal.reason.length }}/500</div>
          </div>
          <div v-if="bulkModal.error" class="modal-error">{{ bulkModal.error }}</div>
          <div v-if="bulkModal.progress > 0" class="progress-bar-container">
            <div class="progress-bar" :style="{ width: progressPercent + '%' }"></div>
            <span class="progress-text">{{ bulkModal.progress }} / {{ selectedIds.size }}</span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" :disabled="bulkModal.processing" @click="closeBulkModal">{{ $t('common.cancel') }}</button>
          <button
            class="btn"
            :class="bulkModal.action === 'suspend' ? 'btn-warning' : 'btn-danger'"
            :disabled="!bulkModal.reason.trim() || bulkModal.processing"
            @click="executeBulkAction"
          >
            <span v-if="bulkModal.processing" class="spinner spinner-sm"></span>
            {{ bulkModal.processing ? $t('common.processing') : (bulkModal.action === 'suspend' ? $t('admin.players.bulk_modal_suspend_all') : $t('admin.players.bulk_modal_ban_all')) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()

const router = useRouter()
const toast = useToastStore()

// ----------- Types -----------

interface Player {
  id: number
  username: string
  email: string
  status: string
  kyc_status: string
  vip_level: number
  balance: number
  total_deposited: number
  total_wagered: number
  country: string | null
  created_at: string
  last_login_at: string | null
}

interface PaginatedResponse {
  data: Player[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

// ----------- State -----------

const players = ref<Player[]>([])
const loading = ref(true)
const currentPage = ref(1)
const totalPages = ref(0)
const totalPlayers = ref(0)
const perPage = 25

const filters = ref({
  search: '',
  status: '',
  kyc_status: '',
  vip_level: '',
})

const joinedFilter = ref('')

const sortField = ref('created_at')
const sortDirection = ref<'asc' | 'desc'>('desc')

// ----------- Selection State -----------

const selectedIds = ref<Set<number>>(new Set())
const bulkMenuOpen = ref(false)

const bulkModal = reactive({
  visible: false,
  action: '' as 'suspend' | 'ban',
  reason: '',
  error: '',
  processing: false,
  progress: 0,
})

// ----------- Computed -----------

const rangeStart = computed(() => {
  if (totalPlayers.value === 0) return 0
  return (currentPage.value - 1) * perPage + 1
})

const rangeEnd = computed(() => {
  return Math.min(currentPage.value * perPage, totalPlayers.value)
})

const visiblePages = computed(() => {
  const pages: number[] = []
  const total = totalPages.value
  const current = currentPage.value

  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)

  if (end - start < 4) {
    if (start === 1) {
      end = Math.min(total, start + 4)
    } else {
      start = Math.max(1, end - 4)
    }
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

const allPageSelected = computed(() => {
  if (players.value.length === 0) return false
  return players.value.every((p) => selectedIds.value.has(p.id))
})

const somePageSelected = computed(() => {
  if (players.value.length === 0) return false
  const some = players.value.some((p) => selectedIds.value.has(p.id))
  return some && !allPageSelected.value
})

const selectedPlayers = computed(() => {
  return players.value.filter((p) => selectedIds.value.has(p.id))
})

const progressPercent = computed(() => {
  if (selectedIds.value.size === 0) return 0
  return Math.round((bulkModal.progress / selectedIds.value.size) * 100)
})

// ----------- Selection Logic -----------

function toggleSelectPlayer(id: number) {
  const newSet = new Set(selectedIds.value)
  if (newSet.has(id)) {
    newSet.delete(id)
  } else {
    newSet.add(id)
  }
  selectedIds.value = newSet
}

function toggleSelectAll() {
  if (allPageSelected.value) {
    // Deselect all on current page
    const newSet = new Set(selectedIds.value)
    players.value.forEach((p) => newSet.delete(p.id))
    selectedIds.value = newSet
  } else {
    // Select all on current page
    const newSet = new Set(selectedIds.value)
    players.value.forEach((p) => newSet.add(p.id))
    selectedIds.value = newSet
  }
}

function clearSelection() {
  selectedIds.value = new Set()
  bulkMenuOpen.value = false
}

// ----------- Bulk Actions -----------

function toggleBulkMenu() {
  bulkMenuOpen.value = !bulkMenuOpen.value
}

function closeBulkMenuOnOutside() {
  bulkMenuOpen.value = false
}

function openBulkAction(action: 'suspend' | 'ban') {
  bulkMenuOpen.value = false
  bulkModal.action = action
  bulkModal.reason = ''
  bulkModal.error = ''
  bulkModal.processing = false
  bulkModal.progress = 0
  bulkModal.visible = true
}

function closeBulkModal() {
  if (bulkModal.processing) return
  bulkModal.visible = false
}

async function executeBulkAction(): Promise<void> {
  if (!bulkModal.reason.trim()) return

  bulkModal.processing = true
  bulkModal.error = ''
  bulkModal.progress = 0

  const ids = Array.from(selectedIds.value)
  const endpoint = bulkModal.action === 'suspend' ? 'suspend' : 'ban'
  let successCount = 0
  let failCount = 0

  for (const id of ids) {
    try {
      await apiClient.post(`/admin/api/players/${id}/${endpoint}`, {
        reason: bulkModal.reason.trim(),
      })
      successCount++
    } catch {
      failCount++
    }
    bulkModal.progress++
  }

  bulkModal.processing = false
  bulkModal.visible = false

  if (failCount === 0) {
    if (bulkModal.action === 'suspend') {
      toast.success(successCount > 1
        ? t('admin.players.bulk_success_suspended_plural', { n: successCount })
        : t('admin.players.bulk_success_suspended', { n: successCount }))
    } else {
      toast.success(successCount > 1
        ? t('admin.players.bulk_success_banned_plural', { n: successCount })
        : t('admin.players.bulk_success_banned', { n: successCount }))
    }
  } else {
    toast.warning(t('admin.players.bulk_partial', { success: successCount, failed: failCount }))
  }

  selectedIds.value = new Set()
  fetchPlayers()
}

function exportSelectedCsv() {
  bulkMenuOpen.value = false

  const selected = players.value.filter((p) => selectedIds.value.has(p.id))
  if (selected.length === 0) return

  downloadCsv(selected, 'players-selected')
}

// ----------- Data Fetching -----------

let debounceTimer: ReturnType<typeof setTimeout> | null = null

function debouncedFetch() {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    currentPage.value = 1
    fetchPlayers()
  }, 300)
}

function resetAndFetch() {
  currentPage.value = 1
  fetchPlayers()
}

function applyJoinedFilter() {
  currentPage.value = 1
  fetchPlayers()
}

async function fetchPlayers(): Promise<void> {
  loading.value = true
  try {
    const params: Record<string, string | number> = {
      page: currentPage.value,
      sort: sortField.value,
      direction: sortDirection.value,
    }

    if (filters.value.search) params.search = filters.value.search
    if (filters.value.status) params.status = filters.value.status
    if (filters.value.kyc_status) params.kyc_status = filters.value.kyc_status
    if (filters.value.vip_level) params.vip_level = filters.value.vip_level

    // Compute date_from from joined filter
    if (joinedFilter.value) {
      const now = new Date()
      const daysBack = joinedFilter.value === '7d' ? 7 : joinedFilter.value === '30d' ? 30 : 90
      const from = new Date(now.getTime() - daysBack * 86400000)
      params.date_from = from.toISOString().split('T')[0]
    }

    const response = await apiClient.get<PaginatedResponse>('/admin/api/players', { params })
    const data = response.data

    players.value = data.data
    currentPage.value = data.current_page
    totalPages.value = data.last_page
    totalPlayers.value = data.total
  } catch {
    toast.error(t('admin.players.error_load'))
  } finally {
    loading.value = false
  }
}

function exportCsv(): void {
  downloadCsv(players.value, 'players-export')
}

function downloadCsv(data: Player[], filenamePrefix: string): void {
  const headers = ['Username', 'Email', 'Status', 'KYC', 'VIP Level', 'Balance', 'Wagered', 'Country', 'Joined']
  const rows = data.map((p) => [
    p.username,
    p.email,
    p.status,
    p.kyc_status,
    String(p.vip_level),
    (p.balance / 100).toFixed(2),
    (p.total_wagered / 100).toFixed(2),
    p.country ?? '',
    p.created_at.split('T')[0],
  ])

  const csvContent = [headers, ...rows].map((r) => r.map((v) => `"${v}"`).join(',')).join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filenamePrefix}-${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ----------- Sorting -----------

function toggleSort(field: string) {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDirection.value = 'desc'
  }
  currentPage.value = 1
  fetchPlayers()
}

function sortClass(field: string): string {
  return sortField.value === field ? 'sorted' : ''
}

function sortIcon(field: string): string {
  if (sortField.value !== field) return '\u2195'
  return sortDirection.value === 'asc' ? '\u2191' : '\u2193'
}

// ----------- Formatting -----------

function formatCents(cents: number): string {
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function getInitials(username: string): string {
  return username
    .split(/[_.\-\s]/)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'active': return 'badge-active'
    case 'suspended': return 'badge-suspended'
    case 'banned': return 'badge-suspended'
    default: return 'badge-pending'
  }
}

function kycBadgeClass(kycStatus: string): string {
  switch (kycStatus) {
    case 'verified': return 'badge-verified'
    case 'pending': return 'badge-pending-kyc'
    case 'unverified': return 'badge-unverified'
    case 'rejected': return 'badge-suspended'
    default: return 'badge-unverified'
  }
}

function wagerClass(wagered: number): string {
  if (wagered >= 5000000) return 'wager-gold'
  if (wagered >= 1000000) return 'wager-high'
  return ''
}

// ----------- Navigation -----------

function goToPlayer(id: number) {
  router.push({ path: `/admin/players/${id}` })
}

function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  fetchPlayers()
}

// ----------- Lifecycle -----------

onMounted(() => {
  fetchPlayers()
  document.addEventListener('click', closeBulkMenuOnOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeBulkMenuOnOutside)
})
</script>

<style scoped>
.players-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.selected-count {
  font-size: 13px;
  font-weight: 600;
  color: var(--accent-secondary, #1475e1);
  background: rgba(20, 117, 225, 0.15);
  padding: 6px 12px;
  border-radius: var(--radius-full, 9999px);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

.btn-ghost {
  background: transparent;
  color: var(--text-tertiary, #7f8fa4);
  padding: 9px 12px;
}

.btn-ghost:hover {
  color: var(--text-primary, #fff);
}

.btn-warning {
  background: var(--color-warning, #ffa726);
  color: #000;
}

.btn-warning:hover:not(:disabled) {
  background: #e69520;
}

.btn-danger {
  background: var(--color-danger, #ed4245);
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  background: #d63638;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dropdown-arrow {
  font-size: 10px;
  margin-left: 2px;
}

/* Bulk Actions Dropdown */
.bulk-actions-wrapper {
  position: relative;
}

.bulk-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 200px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  z-index: 50;
  overflow: hidden;
}

.bulk-dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  transition: all 150ms ease;
  text-align: left;
}

.bulk-dropdown-item:hover {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
}

.bulk-dropdown-danger:hover {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.bulk-dropdown-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
}

.bulk-icon {
  font-size: 14px;
  width: 18px;
  text-align: center;
}

/* Filter Bar */
.filter-bar {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  white-space: nowrap;
}

.filter-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  width: 180px;
  transition: border-color 150ms ease;
}

.filter-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.filter-input::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

.filter-bar select {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  width: 140px;
  transition: border-color 150ms ease;
}

.filter-bar select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.filter-bar select option {
  background: var(--bg-secondary, #1a2c38);
  color: var(--text-primary, #fff);
}

/* Loading */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 64px 20px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-sm {
  width: 14px;
  height: 14px;
  border-width: 2px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Table */
.table-container {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  transition: border-color 150ms ease;
}

.table-container:hover {
  border-color: rgba(255, 255, 255, 0.1);
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: var(--bg-tertiary, #213743);
}

th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  white-space: nowrap;
}

th.sortable {
  cursor: pointer;
  user-select: none;
  transition: color 150ms ease;
}

th.sortable:hover {
  color: var(--text-primary, #fff);
}

th.sorted {
  color: var(--accent-primary, #00e701);
}

.sort-icon {
  font-size: 10px;
  margin-left: 4px;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

.player-row {
  background: var(--bg-secondary, #1a2c38);
  transition: background 150ms ease;
  cursor: pointer;
}

.player-row:hover {
  background: #253d4f;
}

.player-row.row-selected {
  background: rgba(20, 117, 225, 0.08);
}

.player-row.row-selected:hover {
  background: rgba(20, 117, 225, 0.14);
}

tbody tr:last-child td {
  border-bottom: none;
}

.empty-row {
  text-align: center;
  color: var(--text-tertiary, #7f8fa4);
  padding: 48px 16px !important;
}

/* Checkbox styling */
input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--accent-secondary, #1475e1);
}

/* Player Cell */
.player-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.player-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1475e1, #9b59b6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.player-name {
  font-size: 13px;
  color: var(--text-primary, #fff);
  font-weight: 600;
}

.player-sub {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Monetary Values */
.mono-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--text-primary, #fff);
}

.wager-high {
  color: var(--color-success, #00e701);
}

.wager-gold {
  color: var(--accent-gold, #ffd700);
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.badge-active {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-suspended {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.badge-pending {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-verified {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-unverified {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-pending-kyc {
  background: rgba(20, 117, 225, 0.15);
  color: var(--accent-secondary, #1475e1);
}

/* Actions */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  font-size: 14px;
  color: var(--text-tertiary, #7f8fa4);
}

.btn-icon:hover {
  background: rgba(0, 231, 1, 0.15);
  color: var(--accent-primary, #00e701);
  border-color: rgba(0, 231, 1, 0.3);
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled) {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.active {
  background: var(--accent-primary, #00e701);
  color: #000;
  border-color: var(--accent-primary, #00e701);
}

.pagination-info {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 12px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.15s ease;
}

.modal-content {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg, 12px);
  width: 100%;
  max-width: 500px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
  font-family: inherit;
}

.modal-close:hover {
  color: var(--text-primary, #fff);
}

.modal-body {
  padding: 20px 24px;
}

.modal-description {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  margin: 0 0 16px;
  line-height: 1.5;
}

.modal-affected {
  margin-bottom: 16px;
}

.affected-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  margin-bottom: 8px;
}

.affected-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.affected-player {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  background: var(--bg-tertiary, #213743);
  padding: 4px 10px;
  border-radius: var(--radius-full, 9999px);
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 6px;
}

.required {
  color: var(--color-danger, #ed4245);
}

.form-textarea {
  width: 100%;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 150ms ease;
}

.form-textarea:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.form-textarea::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

.char-count {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  text-align: right;
  margin-top: 4px;
}

.modal-error {
  font-size: 13px;
  color: var(--color-danger, #ed4245);
  background: rgba(237, 66, 69, 0.1);
  padding: 10px 12px;
  border-radius: var(--radius-md, 8px);
  margin-bottom: 12px;
}

.progress-bar-container {
  position: relative;
  height: 28px;
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
  margin-bottom: 12px;
}

.progress-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: var(--accent-secondary, #1475e1);
  transition: width 200ms ease;
  border-radius: var(--radius-md, 8px);
}

.progress-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  z-index: 1;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* Column widths */
.col-checkbox { width: 40px; }
.col-player { width: 20%; }
.col-email { width: 17%; }
.col-balance { width: 11%; }
.col-wagered { width: 11%; }
.col-status { width: 10%; }
.col-kyc { width: 10%; }
.col-joined { width: 10%; }
.col-actions { width: 6%; }

/* Responsive */
@media (max-width: 1200px) {
  .filter-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  table {
    font-size: 12px;
  }

  td,
  th {
    padding: 10px 12px;
  }
}

@media (max-width: 768px) {
  .players-page {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .filter-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-input {
    width: 100%;
  }
}

/* =========================================================
   RTL overrides
   ========================================================= */
[dir='rtl'] th {
  text-align: right;
}

/* Monetary/numeric values always stay LTR */
[dir='rtl'] .mono-value {
  direction: ltr;
  unicode-bidi: embed;
  display: inline-block;
}

/* Bulk dropdown: flip anchor from right to left */
[dir='rtl'] .bulk-dropdown {
  right: auto;
  left: 0;
}

[dir='rtl'] .bulk-dropdown-item {
  text-align: right;
}

/* Modal text alignment */
[dir='rtl'] .modal-title,
[dir='rtl'] .modal-description,
[dir='rtl'] .form-label,
[dir='rtl'] .affected-label {
  text-align: right;
}

[dir='rtl'] .char-count {
  text-align: left;
}
</style>
